/** Herramientas de creación: nota adhesiva, cuadro de texto y figuras. */
import type { GeoShape } from '../../shared/types.ts'
import { geoPath } from '../canvas/render-shape.ts'
import { createGeo, createNote, createText, NOTE_DEFAULT_SIZE } from '../state/shapes.ts'
import type { AppContext, PointerInfo, Tool } from './types.ts'

/** Nota adhesiva: un clic la coloca; arrastrar define su tamaño. */
export class NoteTool implements Tool {
  readonly id = 'note' as const
  readonly cursor = 'copy'
  private ctx: AppContext
  private start: { x: number; y: number } | null = null
  private current = { x: 0, y: 0 }
  private removePainter: (() => void) | null = null

  constructor(ctx: AppContext) {
    this.ctx = ctx
  }

  onActivate(): void {
    this.removePainter = this.ctx.renderer.addPainter((c) => this.paint(c))
  }

  onDeactivate(): void {
    this.removePainter?.()
    this.removePainter = null
    this.start = null
  }

  onPointerDown(p: PointerInfo): void {
    this.start = { x: p.x, y: p.y }
    this.current = { x: p.x, y: p.y }
  }

  onPointerMove(p: PointerInfo): void {
    if (!this.start) return
    this.current = { x: p.x, y: p.y }
    this.ctx.renderer.invalidateOverlay()
  }

  onPointerUp(): void {
    if (!this.start) return
    const dragged = Math.hypot(this.current.x - this.start.x, this.current.y - this.start.y)
    const size = dragged > 24 ? Math.max(80, dragged) : NOTE_DEFAULT_SIZE
    const x = dragged > 24 ? Math.min(this.start.x, this.current.x) : this.start.x - size / 2
    const y = dragged > 24 ? Math.min(this.start.y, this.current.y) : this.start.y - size / 2
    const note = createNote({
      x,
      y,
      color: this.ctx.store.style.noteColor,
      createdBy: this.ctx.userId,
      author: this.ctx.getUserName(),
      z: this.ctx.board.maxZ() + 1,
      size,
    })
    this.start = null
    this.ctx.board.put(note)
    this.ctx.setTool('select')
    this.ctx.store.setSelection([note.id])
    this.ctx.editText(note.id)
    this.ctx.touch()
  }

  onCancel(): void {
    this.start = null
  }

  private paint(c: CanvasRenderingContext2D): void {
    if (!this.start) return
    const dragged = Math.hypot(this.current.x - this.start.x, this.current.y - this.start.y)
    if (dragged < 24) return
    const x = Math.min(this.start.x, this.current.x)
    const y = Math.min(this.start.y, this.current.y)
    c.save()
    c.globalAlpha = 0.7
    c.fillStyle = this.ctx.store.style.noteColor
    c.beginPath()
    c.roundRect(x, y, Math.abs(this.current.x - this.start.x), Math.abs(this.current.y - this.start.y), 4)
    c.fill()
    c.restore()
  }
}

/** Cuadro de texto: clic para empezar a escribir; arrastrar define el ancho. */
export class TextTool implements Tool {
  readonly id = 'text' as const
  readonly cursor = 'text'
  private ctx: AppContext

  constructor(ctx: AppContext) {
    this.ctx = ctx
  }

  onPointerDown(): void {
    /* la creación ocurre al soltar */
  }

  onPointerMove(): void {
    /* sin estado intermedio */
  }

  onPointerUp(p: PointerInfo): void {
    const shape = createText({
      x: p.x,
      y: p.y - this.ctx.store.style.textSize * 0.7,
      color: this.ctx.store.style.textColor,
      fontSize: this.ctx.store.style.textSize,
      createdBy: this.ctx.userId,
      z: this.ctx.board.maxZ() + 1,
    })
    this.ctx.board.put(shape)
    this.ctx.setTool('select')
    this.ctx.store.setSelection([shape.id])
    this.ctx.editText(shape.id)
    this.ctx.touch()
  }
}

/** Figuras: arrastrar para dibujarlas; un clic coloca un tamaño por defecto. */
export class ShapeTool implements Tool {
  readonly id = 'shape' as const
  readonly cursor = 'crosshair'
  private ctx: AppContext
  private start: { x: number; y: number } | null = null
  private current = { x: 0, y: 0 }
  private removePainter: (() => void) | null = null

  constructor(ctx: AppContext) {
    this.ctx = ctx
  }

  onActivate(): void {
    this.removePainter = this.ctx.renderer.addPainter((c) => this.paint(c))
  }

  onDeactivate(): void {
    this.removePainter?.()
    this.removePainter = null
    this.start = null
  }

  onPointerDown(p: PointerInfo): void {
    this.start = { x: p.x, y: p.y }
    this.current = { x: p.x, y: p.y }
  }

  onPointerMove(p: PointerInfo): void {
    if (!this.start) return
    this.current = { x: p.x, y: p.y }
    this.ctx.renderer.invalidateOverlay()
  }

  onPointerUp(p: PointerInfo): void {
    if (!this.start) return
    const { style } = this.ctx.store
    const isLine = style.shapeKind === 'line' || style.shapeKind === 'arrow'
    let x = this.start.x
    let y = this.start.y
    let w = this.current.x - this.start.x
    let h = this.current.y - this.start.y

    if (Math.hypot(w, h) < 12) {
      // Clic simple: figura de tamaño estándar centrada en el punto.
      w = isLine ? 160 : 180
      h = isLine ? 0 : 140
      if (!isLine) {
        x -= w / 2
        y -= h / 2
      }
    } else if (!isLine) {
      if (p.shiftKey) {
        const s = Math.max(Math.abs(w), Math.abs(h))
        w = Math.sign(w) * s
        h = Math.sign(h) * s
      }
      x = Math.min(this.start.x, this.start.x + w)
      y = Math.min(this.start.y, this.start.y + h)
      w = Math.abs(w)
      h = Math.abs(h)
    } else if (p.shiftKey) {
      // Líneas rectas en incrementos de 45°.
      const angle = Math.round(Math.atan2(h, w) / (Math.PI / 4)) * (Math.PI / 4)
      const len = Math.hypot(w, h)
      w = Math.cos(angle) * len
      h = Math.sin(angle) * len
    }

    const shape = createGeo({
      kind: style.shapeKind,
      x,
      y,
      w,
      h,
      color: style.shapeColor,
      fill: style.shapeFill,
      strokeWidth: style.shapeStrokeWidth,
      createdBy: this.ctx.userId,
      z: this.ctx.board.maxZ() + 1,
    })
    this.start = null
    this.ctx.board.put(shape)
    this.ctx.setTool('select')
    this.ctx.store.setSelection([shape.id])
    this.ctx.touch()
  }

  onCancel(): void {
    this.start = null
  }

  private paint(c: CanvasRenderingContext2D): void {
    if (!this.start) return
    const { style } = this.ctx.store
    const isLine = style.shapeKind === 'line' || style.shapeKind === 'arrow'
    const w = this.current.x - this.start.x
    const h = this.current.y - this.start.y
    if (Math.hypot(w, h) < 4) return
    const x = isLine ? this.start.x : Math.min(this.start.x, this.current.x)
    const y = isLine ? this.start.y : Math.min(this.start.y, this.current.y)
    const preview: GeoShape = {
      id: 'preview',
      type: 'shape',
      kind: style.shapeKind,
      x,
      y,
      w: isLine ? w : Math.abs(w),
      h: isLine ? h : Math.abs(h),
      rotation: 0,
      z: 0,
      createdBy: '',
      updatedAt: 0,
      color: style.shapeColor,
      fill: style.shapeFill,
      strokeWidth: style.shapeStrokeWidth,
    }
    c.save()
    c.translate(preview.x, preview.y)
    c.globalAlpha = 0.85
    const path = geoPath(preview.kind, preview.w, preview.h)
    if (preview.fill !== 'none' && !isLine) {
      c.fillStyle = preview.fill
      c.fill(path)
    }
    c.strokeStyle = preview.color
    c.lineWidth = preview.strokeWidth
    c.lineJoin = 'round'
    c.lineCap = 'round'
    c.stroke(path)
    c.restore()
  }
}
